import type { ReactElement, ReactNode } from 'react';
import { Tooltip as RTooltip } from 'radix-ui';
import { cn } from './cn';

/** Montar una vez en la raíz de la app (la galería ya lo hace). Demora 400 ms (§6.20). */
export function TooltipProvider({ children, delayDuration = 400 }: { children: ReactNode; delayDuration?: number }) {
  return (
    <RTooltip.Provider delayDuration={delayDuration} skipDelayDuration={200}>
      {children}
    </RTooltip.Provider>
  );
}

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Abierto controlado (raro: casi siempre se deja a Radix). */
  open?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Tooltip de texto: nombra botones de ícono y explica abreviaturas. Nunca contiene información
 * imprescindible ni controles. Tinta invertida, radio 4, máx. 240 px.
 */
export function Tooltip({ content, children, side = 'top', align = 'center', open, disabled, className }: TooltipProps) {
  if (disabled || content == null || content === '') return children;
  return (
    <RTooltip.Root open={open}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          data-motion="scale"
          className={cn(
            'z-(--z-tooltip) max-w-60 rounded-xs bg-ink px-2 py-1 text-caption text-bg',
            'data-[state=closed]:animate-fade-out data-[state=delayed-open]:animate-fade-in data-[state=instant-open]:animate-fade-in',
            className,
          )}
        >
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}
